import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Link, useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Eye,
  BarChart3,
  Shield,
  FileSearch,
  Calendar,
  Target,
  Users,
  Building2,
  Leaf,
  MapPin,
  X,
  Sparkles,
  FileText,
  Radio
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  RadialBarChart,
  RadialBar,
  Treemap
} from 'recharts';
import AIAssistantModal from '../components/AIAssistantModal';
import DeadlineWidget from '../components/DeadlineWidget';

// View Mode Options for CSO
const VIEW_MODES = [
  { id: 'summary', label: 'Executive Summary', icon: Eye },
  { id: 'compliance', label: 'Compliance Manager View', icon: Shield },
  { id: 'analyst', label: 'Data Analyst View', icon: BarChart3 },
  { id: 'auditor', label: 'Auditor View', icon: FileSearch }
];

// Compliance Score Ring
const ComplianceScoreRing = ({ score, size = 160 }) => {
  const data = [{ name: 'Score', value: score, fill: score >= 70 ? '#059669' : score >= 40 ? '#D97706' : '#DC2626' }];
  
  return (
    <div className="relative" style={{ width: size, height: size, minWidth: size, minHeight: size }}>
      <ResponsiveContainer width={size} height={size} minWidth={size} minHeight={size}>
        <RadialBarChart 
          cx="50%" cy="50%" 
          innerRadius="75%" outerRadius="100%" 
          data={data}
          startAngle={90} endAngle={-270}
        >
          <RadialBar background={{ fill: '#E2E8F0' }} dataKey="value" cornerRadius={10} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {score}%
        </span>
        <span className="text-xs text-slate-500 uppercase tracking-wider">Overall</span>
      </div>
    </div>
  );
};

// Drill-Down Card Component
const DrillDownCard = ({ title, value, subtitle, icon: Icon, color, children, onExpand, isExpanded }) => {
  const colorClasses = {
    emerald: 'bg-emerald-50 border-emerald-200 hover:border-emerald-400',
    blue: 'bg-blue-50 border-blue-200 hover:border-blue-400',
    amber: 'bg-amber-50 border-amber-200 hover:border-amber-400',
    red: 'bg-red-50 border-red-200 hover:border-red-400',
    purple: 'bg-purple-50 border-purple-200 hover:border-purple-400'
  };
  const iconClasses = {
    emerald: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600'
  };

  return (
    <div className={`rounded-xl border-2 transition-all duration-300 ${colorClasses[color]} ${isExpanded ? 'col-span-2 row-span-2' : ''}`}>
      <button
        onClick={onExpand}
        className="w-full p-5 text-left"
        data-testid={`drilldown-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconClasses[color]}`}>
              <Icon size={20} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
              <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
              {subtitle && <p className="text-sm text-slate-600">{subtitle}</p>}
            </div>
          </div>
          {children && (
            <div className="text-slate-400">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          )}
        </div>
      </button>
      
      {isExpanded && children && (
        <div className="px-5 pb-5 pt-2 border-t border-current/10">
          {children}
        </div>
      )}
    </div>
  );
};

// Custom Treemap Content renderer - extracted to avoid nested component issue
const CustomTreemapContent = ({ x, y, width, height, name, severity }) => {
  const COLORS = {
    critical: '#DC2626',
    high: '#F59E0B',
    medium: '#3B82F6',
    low: '#10B981'
  };
  
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={COLORS[severity] || '#94A3B8'}
        rx={4}
      />
      {width > 50 && height > 30 && (
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          fill="#fff"
          fontSize={12}
          fontWeight="bold"
        >
          {name}
        </text>
      )}
    </g>
  );
};

// Risk Heat Map Component
const RiskHeatMap = ({ risks }) => {
  const riskData = [
    { name: 'Supply Chain', size: 35, severity: 'high' },
    { name: 'Regulatory', size: 25, severity: 'critical' },
    { name: 'Climate', size: 20, severity: 'medium' },
    { name: 'Reputation', size: 15, severity: 'low' },
    { name: 'Data Quality', size: 5, severity: 'medium' }
  ];

  return (
    <div style={{ height: 192, minHeight: 192 }}>
      <ResponsiveContainer width="100%" height={192} minHeight={192}>
        <Treemap
          data={riskData}
          dataKey="size"
          aspectRatio={4/3}
          stroke="#fff"
          fill="#8884d8"
          content={<CustomTreemapContent />}
        />
      </ResponsiveContainer>
    </div>
  );
};

// Value Chain Overview Component
const ValueChainOverview = () => {
  const valueChain = [
    { stage: 'Upstream', emissions: 29783, label: 'Scope 3 Upstream', color: '#6366F1' },
    { stage: 'Operations', emissions: 24863, label: 'Scope 1 + 2', color: '#10B981' },
    { stage: 'Downstream', emissions: 15420, label: 'Scope 3 Downstream', color: '#F59E0B' }
  ];

  const total = valueChain.reduce((acc, v) => acc + v.emissions, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-500">Total Value Chain Emissions</span>
        <span className="font-bold text-slate-900">{total.toLocaleString()} tCO2e</span>
      </div>
      <div className="flex h-8 rounded-lg overflow-hidden">
        {valueChain.map((stage, idx) => (
          <div
            key={stage.stage}
            style={{ width: `${(stage.emissions / total) * 100}%`, backgroundColor: stage.color }}
            className="flex items-center justify-center text-white text-xs font-medium"
          >
            {Math.round((stage.emissions / total) * 100)}%
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        {valueChain.map((stage) => (
          <div key={stage.stage} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: stage.color }} />
            <span className="text-slate-600">{stage.stage}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ExecutiveHub = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('summary');
  const [expandedCard, setExpandedCard] = useState(null);
  const [summary, setSummary] = useState(null);
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // AI Board Report State
  const [showBoardReportModal, setShowBoardReportModal] = useState(false);
  const [boardReportLoading, setBoardReportLoading] = useState(false);
  const [boardReportResult, setBoardReportResult] = useState(null);
  const [boardReportError, setBoardReportError] = useState(null);
  const [useStreaming, setUseStreaming] = useState(true); // Default to streaming

  useEffect(() => {
    fetchData();
    logAccess();
  }, []);

  const logAccess = async () => {
    // Silent failure for audit logging
    await apiFetch(`${API}/audit/log-access`, {
      method: 'POST',
      body: {
        dashboard_id: 'executive',
        dashboard_name: 'Executive Hub',
        action: 'dashboard_view',
        timestamp: new Date().toISOString()
      }
    });
  };

  const fetchData = async () => {
    const [summaryData, complianceData] = await Promise.all([
      apiFetch(`${API}/dashboard/summary`),
      apiFetch(`${API}/compliance/quick-status`)
    ]);

    if (summaryData?._error) {
      console.error('Error fetching data:', summaryData.message);
      setLoading(false);
      return;
    }

    setSummary(summaryData);
    setComplianceStatus(complianceData?._error ? {} : complianceData);
    setLoading(false);
  };

  const toggleExpand = (cardId) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  // Generate Board Report with AI
  const generateBoardReport = async () => {
    setBoardReportLoading(true);
    setBoardReportError(null);
    const data = await apiFetch(`${API}/ai/executive/board-report`, {
      method: 'POST',
      body: {
        report_period: 'Q4 2024',
        include_sections: ['compliance', 'risks', 'emissions', 'outlook'],
        executive_summary_only: false
      }
    });
    if (data?._error) {
      setBoardReportError(data.message);
    } else {
      setBoardReportResult(data);
    }
    setBoardReportLoading(false);
  };

  const complianceScore = complianceStatus?.quick_score || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 rounded-lg bg-emerald-700 flex items-center justify-center animate-pulse">
          <Leaf size={24} className="text-white" />
        </div>
      </div>
    );
  }

  // Trend data
  const trendData = [
    { month: 'Jul', score: 25 },
    { month: 'Aug', score: 32 },
    { month: 'Sep', score: 38 },
    { month: 'Oct', score: 42 },
    { month: 'Nov', score: 48 },
    { month: 'Dec', score: Math.round(complianceScore) }
  ];

  return (
    <div className="space-y-6" data-testid="executive-hub">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard-selector')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Executive Hub
            </h1>
            <p className="text-slate-600">Strategic ESG overview for leadership</p>
          </div>
        </div>

        {/* View Mode Selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 mr-2">View:</span>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            data-testid="view-mode-selector"
            className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {VIEW_MODES.map((mode) => (
              <option key={mode.id} value={mode.id}>{mode.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* View Mode Banner */}
      {viewMode !== 'summary' && (
        <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-3">
            <Eye size={20} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              Viewing as: {VIEW_MODES.find(m => m.id === viewMode)?.label}
            </span>
          </div>
          <button
            onClick={() => setViewMode('summary')}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Return to Executive Summary
          </button>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compliance Score - Large */}
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-6 flex flex-col items-center">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">Overall Compliance</h3>
          <ComplianceScoreRing score={Math.round(complianceScore)} />
          <div className="mt-4 flex items-center gap-2">
            {complianceScore >= 70 ? (
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium flex items-center gap-1">
                <CheckCircle size={14} /> On Track
              </span>
            ) : complianceScore >= 40 ? (
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium flex items-center gap-1">
                <AlertTriangle size={14} /> Needs Attention
              </span>
            ) : (
              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium flex items-center gap-1">
                <AlertTriangle size={14} /> Critical
              </span>
            )}
          </div>
          
          {/* Mini Trend */}
          <div className="w-full mt-6" style={{ height: 80, minHeight: 80 }}>
            <ResponsiveContainer width="100%" height={80} minHeight={80}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="score" stroke="#059669" strokeWidth={2} fill="url(#colorTrend)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-slate-500 mt-2">6-month trend</p>
        </div>

        {/* Board KPIs - Drill Down Cards */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          <DrillDownCard
            title="Critical Risks"
            value={summary?.risks?.critical || 0}
            subtitle={`${summary?.risks?.high || 0} high priority`}
            icon={AlertTriangle}
            color="red"
            isExpanded={expandedCard === 'risks'}
            onExpand={() => toggleExpand('risks')}
          >
            <RiskHeatMap risks={summary?.risks} />
          </DrillDownCard>

          <DrillDownCard
            title="Frameworks"
            value={summary?.frameworks?.length || 0}
            subtitle="Active frameworks"
            icon={Building2}
            color="blue"
            isExpanded={expandedCard === 'frameworks'}
            onExpand={() => toggleExpand('frameworks')}
          >
            <div className="space-y-2">
              {summary?.frameworks?.map((fw, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-slate-100">
                  <span className="text-sm font-medium">{fw.name}</span>
                  <span className="text-xs text-slate-500">{fw.standards?.length || 0} standards</span>
                </div>
              ))}
            </div>
          </DrillDownCard>

          <DrillDownCard
            title="ESG Indicators"
            value={summary?.indicators?.total || 0}
            subtitle="ESRS aligned"
            icon={Target}
            color="emerald"
            isExpanded={expandedCard === 'indicators'}
            onExpand={() => toggleExpand('indicators')}
          >
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-emerald-50 rounded">
                <p className="text-lg font-bold text-emerald-700">{summary?.indicators?.by_category?.Environmental || 0}</p>
                <p className="text-xs text-emerald-600">Environmental</p>
              </div>
              <div className="p-2 bg-pink-50 rounded">
                <p className="text-lg font-bold text-pink-700">{summary?.indicators?.by_category?.Social || 0}</p>
                <p className="text-xs text-pink-600">Social</p>
              </div>
              <div className="p-2 bg-blue-50 rounded">
                <p className="text-lg font-bold text-blue-700">{summary?.indicators?.by_category?.Governance || 0}</p>
                <p className="text-xs text-blue-600">Governance</p>
              </div>
            </div>
          </DrillDownCard>

          <DrillDownCard
            title="Data Coverage"
            value={`${complianceStatus?.total_uploads || 0}`}
            subtitle="Data sources uploaded"
            icon={BarChart3}
            color="purple"
            isExpanded={expandedCard === 'data'}
            onExpand={() => toggleExpand('data')}
          >
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Emissions Data</span>
                <span className={complianceStatus?.emissions_data_status === 'available' ? 'text-emerald-600' : 'text-amber-600'}>
                  {complianceStatus?.emissions_data_status || 'Missing'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Workforce Data</span>
                <span className={complianceStatus?.workforce_data_status === 'available' ? 'text-emerald-600' : 'text-amber-600'}>
                  {complianceStatus?.workforce_data_status || 'Missing'}
                </span>
              </div>
            </div>
          </DrillDownCard>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deadline Widget - Fetches from backend */}
        <DeadlineWidget maxItems={4} showHeader={true} daysAhead={120} />

        {/* Value Chain Overview */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Value Chain Emissions</h3>
            <Link to="/emissions-tracking" className="text-xs text-emerald-600 hover:underline">Details</Link>
          </div>
          <ValueChainOverview />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4">
        <Link
          to="/compliance-center"
          className="flex-1 p-4 bg-emerald-700 text-white rounded-xl hover:bg-emerald-800 transition-colors text-center font-medium"
        >
          Run Gap Analysis
        </Link>
        <button
          onClick={() => {
            setShowBoardReportModal(true);
            setBoardReportResult(null);
            setBoardReportError(null);
            // Only call generateBoardReport if not using streaming
            if (!useStreaming) {
              generateBoardReport();
            }
          }}
          className="flex-1 p-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all text-center font-medium flex items-center justify-center gap-2"
          data-testid="generate-board-report-btn"
        >
          <Sparkles size={18} />
          Generate Board Report
          {useStreaming && <Radio size={14} className="opacity-70" />}
        </button>
        <Link
          to="/audit/trail"
          className="flex-1 p-4 bg-white border border-slate-200 text-slate-900 rounded-xl hover:border-emerald-400 transition-colors text-center font-medium"
        >
          View Audit Trail
        </Link>
      </div>

      {/* AI Board Report Modal - with Streaming Support */}
      <AIAssistantModal
        isOpen={showBoardReportModal}
        onClose={() => setShowBoardReportModal(false)}
        title="AI Board Report Generator"
        icon={FileText}
        iconColor="text-emerald-600"
        iconBg="bg-emerald-100"
        loading={boardReportLoading}
        result={boardReportResult}
        error={boardReportError}
        onRetry={() => {
          setBoardReportResult(null);
          if (useStreaming) {
            // Re-open to trigger streaming
            setShowBoardReportModal(false);
            setTimeout(() => setShowBoardReportModal(true), 100);
          } else {
            generateBoardReport();
          }
        }}
        // Streaming props
        streamUrl={useStreaming ? `${API}/ai/executive/board-report/stream` : null}
        streamParams={{ report_period: 'Q4 2024' }}
      />
    </div>
  );
};

export default ExecutiveHub;
